// Tour Overlay Component for Magnus Resolve
// Displays the tour spotlight and tooltip UI
// ============================================================

import React, { useEffect, useState, useRef } from 'react';
import { useTour, TourStep } from '../contexts/TourContext';
import { X, ChevronLeft, ChevronRight, Shield, Sparkles } from 'lucide-react';

// Helper to calculate tooltip position
const calculateTooltipPosition = (
    step: TourStep,
    rect: DOMRect,
    tooltipWidth: number,
    tooltipHeight: number
) => {
    let top = 0, left = 0;

    switch (step.placement) {
        case 'right':
            top = rect.top + rect.height / 2 - tooltipHeight / 2;
            left = rect.right + 20;
            break;
        case 'left':
            top = rect.top + rect.height / 2 - tooltipHeight / 2;
            left = rect.left - tooltipWidth - 20;
            break;
        case 'bottom':
            top = rect.bottom + 20;
            left = rect.left + rect.width / 2 - tooltipWidth / 2;
            break;
        case 'top':
        default:
            top = rect.top - tooltipHeight - 20;
            left = rect.left + rect.width / 2 - tooltipWidth / 2;
            break;
    }

    // Keep tooltip in viewport
    if (left < 10) left = 10;
    if (left + tooltipWidth > window.innerWidth - 10) left = window.innerWidth - tooltipWidth - 10;
    if (top < 10) top = 10;
    if (top + tooltipHeight > window.innerHeight - 10) top = window.innerHeight - tooltipHeight - 10;

    return { top, left };
};

export const TourOverlay: React.FC = () => {
    const { isTourActive, currentStep, tourSteps, nextStep, previousStep, stopTour } = useTour();
    const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const tooltipRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isTourActive) return;

        let animationFrameId: number;
        let hasScrolled = false;

        const updatePosition = () => {
            const step = tourSteps[currentStep];
            if (!step) return;

            // Handle CENTER placement
            if (step.placement === 'center') {
                setTargetRect({ width: 0, height: 0, top: 0, left: 0, bottom: 0, right: 0, x: 0, y: 0, toJSON: () => { } });

                const tooltipWidth = tooltipRef.current?.offsetWidth || 450;
                const tooltipHeight = tooltipRef.current?.offsetHeight || 300;

                setTooltipPosition({
                    top: window.innerHeight / 2 - tooltipHeight / 2,
                    left: window.innerWidth / 2 - tooltipWidth / 2
                });
            } else {
                const element = document.querySelector(step.target);
                if (element) {
                    // Scroll element into view on first render of this step
                    if (!hasScrolled) {
                        element.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                            inline: 'nearest'
                        });
                        hasScrolled = true;
                        // Wait for scroll to complete before updating position
                        setTimeout(() => {
                            animationFrameId = requestAnimationFrame(updatePosition);
                        }, 300);
                        return;
                    }

                    const rect = element.getBoundingClientRect();
                    setTargetRect(rect);

                    const tooltipWidth = tooltipRef.current?.offsetWidth || 380;
                    const tooltipHeight = tooltipRef.current?.offsetHeight || 220;

                    const position = calculateTooltipPosition(step, rect, tooltipWidth, tooltipHeight);
                    setTooltipPosition(position);
                }
            }

            animationFrameId = requestAnimationFrame(updatePosition);
        };

        updatePosition();

        return () => {
            if (animationFrameId) cancelAnimationFrame(animationFrameId);
        };
    }, [isTourActive, currentStep, tourSteps]);

    if (!isTourActive || !targetRect) return null;

    const step = tourSteps[currentStep];
    const isWelcome = currentStep === 0 || currentStep === tourSteps.length - 1;

    return (
        <>
            {/* Dark overlay */}
            <div
                className="fixed inset-0 bg-black/60 z-[9998] transition-opacity duration-300"
                onClick={stopTour}
            />

            {/* Blue halo spotlight for highlighted elements */}
            {step.placement !== 'center' && targetRect.width > 0 && (
                <div
                    className="fixed z-[9999] pointer-events-none transition-all duration-300"
                    style={{
                        top: targetRect.top - 8,
                        left: targetRect.left - 8,
                        width: targetRect.width + 16,
                        height: targetRect.height + 16,
                        boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.6), 0 0 25px 10px rgba(59, 130, 246, 0.3)',
                        borderRadius: '12px',
                        border: '3px solid #3b82f6',
                        background: 'transparent',
                    }}
                />
            )}

            {/* Tooltip */}
            <div
                ref={tooltipRef}
                className="fixed z-[10000] bg-white dark:bg-slate-800 rounded-xl shadow-2xl transition-all duration-300 overflow-hidden"
                style={{
                    top: tooltipPosition.top,
                    left: tooltipPosition.left,
                    minWidth: '380px',
                    maxWidth: '450px',
                }}
            >
                {/* Header with gradient */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-4 flex items-start justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-white/20 rounded-lg backdrop-blur-md">
                            {isWelcome ? <Shield size={24} className="text-white" /> : <Sparkles size={24} className="text-white" />}
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-white">{step.title}</h3>
                            <p className="text-xs text-white/70 mt-0.5">
                                Step {currentStep + 1} of {tourSteps.length}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={stopTour}
                        className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-full transition-colors"
                        title="Exit Tour"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-5">
                    <p className="text-gray-700 dark:text-gray-200 leading-relaxed">{step.content}</p>
                </div>

                {/* Progress bar */}
                <div className="px-5 pb-3">
                    <div className="flex gap-1.5">
                        {tourSteps.map((_, index) => (
                            <div
                                key={index}
                                className={`h-1.5 rounded-full transition-all duration-300 ${index === currentStep
                                    ? 'bg-blue-500 flex-[2]'
                                    : index < currentStep
                                        ? 'bg-blue-300 flex-1'
                                        : 'bg-gray-200 dark:bg-gray-600 flex-1'
                                    }`}
                            />
                        ))}
                    </div>
                </div>

                {/* Footer with navigation */}
                <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-slate-800/50">
                    <button
                        onClick={previousStep}
                        disabled={currentStep === 0}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white dark:bg-slate-700 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium text-gray-700 dark:text-gray-200"
                    >
                        <ChevronLeft size={16} />
                        Back
                    </button>

                    <button
                        onClick={stopTour}
                        className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    >
                        Skip Tour
                    </button>

                    <button
                        onClick={nextStep}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white transition-all text-sm font-medium shadow-md hover:shadow-lg"
                    >
                        {currentStep === tourSteps.length - 1 ? 'Get Started' : 'Next'}
                        <ChevronRight size={16} />
                    </button>
                </div>
            </div>
        </>
    );
};

export default TourOverlay;
